import { useCallback, useState } from 'react';

import APIHandler from 'config/APIHandler';
import { storageKeys } from 'config/settings.json';
import StorageHandler from 'config/StorageHandler';

import type { CmsNotification } from 'hooks/useNewAndNowNotifications';

import { GlassCard } from '../../GlassCard/GlassCard';
import type { GlassCardSpan, GlassCardVariant } from '../../types';

import './_notificationsCard.scss';
import type { IconName } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import clsx from 'clsx';
import i18n from 'i18n-react';
import { isArray } from 'lodash-es';
import Carousel from 'nuka-carousel';
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';

export type { CmsNotification } from 'hooks/useNewAndNowNotifications';

export interface NotificationsCardProps {
  appId: number;
  notifications: CmsNotification[];
  isLoading: boolean;
  error: string | null;
  span?: GlassCardSpan;
  variant?: GlassCardVariant;
  hideArrows?: boolean;
  lightTheme?: boolean;
}

const typeToColor: Record<CmsNotification['type'], string> = {
  alert: '#FF6666',
  message: '#66CCFF',
  contest: '#66CC66',
  badge: '#CC66CC',
  'content-update': '#FFFF99',
  events: '#FF9966',
};

const typeToIcon: Record<CmsNotification['type'], string> = {
  alert: 'triangle-exclamation',
  message: 'hand-wave',
  contest: 'circle-dollar',
  badge: 'award',
  'content-update': 'memo-circle-check',
  events: 'calendar',
};

/** Maps API `type` to `notifications-card--accent-top-*` suffix; mirrors `typeToColor` fallbacks. */
function accentTopTypeClassSuffix(rawType: string | undefined): string {
  if (rawType && rawType in typeToColor) {
    return rawType;
  }
  return 'message';
}

const FANCYBOX_EXTENSIONS = ['pdf', 'png', 'jpg', 'jpeg'];

function notificationTypeLabel(type: string): string {
  const translated = i18n.translate(`notification.${type}`) as string;
  const key = `notification.${type}`;
  if (!translated || translated === key) {
    return (i18n.translate('notification.other') as string) || 'Notification';
  }
  return translated;
}

interface NotificationSlideContentProps {
  message: string;
  weblink: string;
  onTrackShare: (_url: string) => void;
}

function NotificationSlideContent({ message, weblink, onTrackShare }: NotificationSlideContentProps) {
  const filteredMessage = message.replace(/<p><br><\/p>/gi, '');
  const trimmedLink = weblink.trim();
  const fileExtension = trimmedLink.split('/').pop()?.split('.').pop()?.toLowerCase() ?? '';

  if (!trimmedLink) {
    return <div className="notifications-card__message" dangerouslySetInnerHTML={{ __html: filteredMessage }} />;
  }

  if (FANCYBOX_EXTENSIONS.includes(fileExtension)) {
    return (
      <a
        className="notifications-card__weblink"
        data-fancybox
        data-type="iframe"
        data-src={trimmedLink}
        href={trimmedLink}
        onClick={() => {
          onTrackShare(trimmedLink);
        }}
      >
        <div className="notifications-card__message" dangerouslySetInnerHTML={{ __html: filteredMessage }} />
      </a>
    );
  }

  if (trimmedLink.includes('http')) {
    return (
      <a
        className="notifications-card__weblink"
        target="_blank"
        href={trimmedLink}
        rel="noreferrer"
        onClick={() => {
          onTrackShare(trimmedLink);
        }}
      >
        <div className="notifications-card__message" dangerouslySetInnerHTML={{ __html: filteredMessage }} />
      </a>
    );
  }

  return (
    <Link
      className="notifications-card__weblink"
      to={trimmedLink}
      onClick={() => {
        onTrackShare(trimmedLink);
      }}
    >
      <div className="notifications-card__message" dangerouslySetInnerHTML={{ __html: filteredMessage }} />
    </Link>
  );
}

export function NotificationsCard({
  appId,
  notifications,
  isLoading,
  error,
  span,
  variant: variantProp,
  hideArrows = false,
  lightTheme = false,
}: NotificationsCardProps) {
  const [currentIndex, setCurrentIndex] = useState(0);

  const onTrackShare = useCallback(
    (url: string) => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access -- C4 StorageHandler
      void StorageHandler.load(storageKeys.user).then(
        (raw: unknown) => {
          const userObj = raw as { email?: string } | null | undefined;
          const email = userObj?.email ?? 'Guest';
          void APIHandler.trackEvent(appId, email, 'collateral', 'notification', url);
        },
        () => {
          void APIHandler.trackEvent(appId, 'Guest', 'collateral', 'notification', url);
        },
      );
    },
    [appId],
  );

  const currentType = notifications[currentIndex]?.type;
  const showCarousel = isArray(notifications) && notifications.length > 0;

  const currentWeblink = notifications[currentIndex]?.weblink?.trim() ?? '';
  const headerWrapper = currentWeblink
    ? (children: ReactNode) => {
        const fileExtension = currentWeblink.split('/').pop()?.split('.').pop()?.toLowerCase() ?? '';
        if (FANCYBOX_EXTENSIONS.includes(fileExtension)) {
          return (
            <a
              className="notifications-card__header-link"
              data-fancybox
              data-type="iframe"
              data-src={currentWeblink}
              href={currentWeblink}
              onClick={() => {
                onTrackShare(currentWeblink);
              }}
            >
              {children}
            </a>
          );
        }
        if (currentWeblink.includes('http')) {
          return (
            <a
              className="notifications-card__header-link"
              target="_blank"
              href={currentWeblink}
              rel="noreferrer"
              onClick={() => {
                onTrackShare(currentWeblink);
              }}
            >
              {children}
            </a>
          );
        }
        return (
          <Link
            className="notifications-card__header-link"
            to={currentWeblink}
            onClick={() => {
              onTrackShare(currentWeblink);
            }}
          >
            {children}
          </Link>
        );
      }
    : undefined;

  const typeForLabel = currentType ?? 'other';
  const currentColor = currentType && typeToColor[currentType] ? typeToColor[currentType] : typeToColor.message;
  const currentIcon = currentType ? typeToIcon[currentType] : null;

  const cardTitle = i18n.translate('notification.newAndNow');
  const carouselAria = i18n.translate('notification.newAndNow') as string;
  const loadingMessage = i18n.translate('glassWidgets.notifications.loading');

  const effectiveVariant = variantProp ?? 'accent';

  const typeBadge = showCarousel ? (
    <span className="notifications-card__type-badge" style={{ color: currentColor }}>
      {currentIcon ? (
        <FontAwesomeIcon icon={['far', currentIcon as IconName]} className="notifications-card__type-badge-icon" />
      ) : null}
      <span className={clsx('notifications-card__type-badge-label', 'global__text--subtitle')}>
        {notificationTypeLabel(typeForLabel)}
      </span>
    </span>
  ) : undefined;

  return (
    <GlassCard
      title={cardTitle}
      span={span}
      variant={effectiveVariant}
      lightTheme={lightTheme}
      className={clsx('notifications-card', {
        [`notifications-card--accent-top-${accentTopTypeClassSuffix(currentType)}`]: effectiveVariant === 'accent',
      })}
      headerExtra={typeBadge}
      headerExtraClassName="notifications-card__header-right"
      headerWrapper={headerWrapper}
    >
      {isLoading && <div className="notifications-card__empty">{loadingMessage}</div>}

      {!isLoading && error && <div className="notifications-card__empty notifications-card__error">{error}</div>}

      {!isLoading && !error && showCarousel && (
        <div className="notifications-card__carousel" role="region" aria-label={carouselAria}>
          {/* @ts-expect-error nuka-carousel accepts children; type defs omit it */}
          <Carousel
            autoplay={notifications.length > 1}
            autoplayInterval={15000}
            wrapAround
            pauseOnHover
            slideIndex={currentIndex}
            afterSlide={setCurrentIndex}
            withoutControls={notifications.length === 1}
            renderCenterLeftControls={
              hideArrows
                ? null
                : ({ previousSlide }) => (
                    <button className="notifications-card__nav-btn" onClick={previousSlide} type="button">
                      <FontAwesomeIcon icon={['fas', 'angle-left']} />
                    </button>
                  )
            }
            renderCenterRightControls={
              hideArrows
                ? null
                : ({ nextSlide }) => (
                    <button className="notifications-card__nav-btn" onClick={nextSlide} type="button">
                      <FontAwesomeIcon icon={['fas', 'angle-right']} />
                    </button>
                  )
            }
            defaultControlsConfig={{
              pagingDotsStyle: {
                fill: 'white',
              },
            }}
          >
            {notifications.map(({ id, message, weblink }) => (
              <div key={id} className="notifications-card__slide" aria-live="polite">
                <NotificationSlideContent message={message} weblink={weblink} onTrackShare={onTrackShare} />
              </div>
            ))}
          </Carousel>
        </div>
      )}
    </GlassCard>
  );
}
